import type { RoundAction } from "../../contracts/RoundSnapshot";
import type { ReplayContext } from "../core/ReplayContext";
import type { RoundActionHandler } from "../dispatch/ActionHandlerRegistry";
import { requireStringField, requireMinorAmountField, optionalMinorAmountField, optionalStringField, requireStringArrayField, ActionPayloadValidationError } from "../validation/ReplayPayloadValidation";
import { validateSpinBoundaryStartPayloadOrThrow, validateSpinBoundaryEndPayloadOrThrow } from "../../validation/SpinBoundaryPayloadValidators";
import { CanonicalActionTypeNames } from "../../shared/CanonicalActionTypeNames";
import { parsePresentScatterStatePayload, parseBonusFeatureWonPayload, parsePresentBigWinPayload } from "../validation";
import type {
  GridWinHighlightInput,
  MultiplierAddedInput,
  NeedleAwardInput,
  PresentBigWinInput,
  ScatterStatePresentationInput,
  SpinBoundaryEndInput,
  SpinBoundaryStartInput,
  SymbolPopValueInput,
  VisibleMultiplierEntry,
} from "./ActionInputs";

/**
 * The canonical action handlers: one per canonical action type, each validating the payload and
 * handing the typed input to the presenter callback it was constructed with. A plugin (shell,
 * mechanic or game) constructs the ones it presents; the engine owns the validation (plan §2.7).
 * Every amount of money is refused unless it is whole minor units — the presenters turn them into
 * BigInt, and until 2026-09-26 five of them took any text and showed a malformed one as nothing.
 */

/**
 * "grid_win_highlight"
 * Payload: positionMask, winAmount (required); symbolIdentifier, symbolCount (optional); lines
 * (optional) — win-group member paylines, each { positionMask (required), winAmount?, symbolCount?,
 * baseWinAmount?, multiplierApplied? }.
 */
export class GridWinHighlightActionHandler implements RoundActionHandler {
  public readonly actionTypeName: string = CanonicalActionTypeNames.gridWinHighlight;

  public constructor(private readonly present: (input: GridWinHighlightInput, context: ReplayContext) => Promise<void>) {}

  public async handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void> {
    const payload = roundAction.actionPayload;

    const positionMask = requireStringField(this.actionTypeName, payload, "positionMask");
    const winAmount = requireMinorAmountField(this.actionTypeName, payload, "winAmount");

    const symbolIdentifier = optionalStringField(payload, "symbolIdentifier");
    const symbolCount = optionalStringField(payload, "symbolCount");

    let lines: GridWinHighlightInput["lines"];
    const rawLines = payload["lines"];
    if (rawLines !== undefined) {
      if (!Array.isArray(rawLines)) {
        throw new ActionPayloadValidationError(this.actionTypeName, "lines", "must be an array when provided.");
      }
      lines = rawLines.map((entry, i) => {
        if (!entry || typeof entry !== "object") {
          throw new ActionPayloadValidationError(this.actionTypeName, `lines[${i}]`, "must be an object.");
        }
        const e = entry as Record<string, unknown>;
        const linePath = `${this.actionTypeName}.lines[${i}]`;
        return {
          positionMask: requireStringField(linePath, e, "positionMask"),
          winAmount: optionalMinorAmountField(linePath, e, "winAmount"),
          symbolCount: optionalStringField(e, "symbolCount"),
          baseWinAmount: optionalMinorAmountField(linePath, e, "baseWinAmount"),
          multiplierApplied: optionalStringField(e, "multiplierApplied"),
        };
      });
    }

    await this.present({ positionMask, winAmount, symbolIdentifier, symbolCount, lines }, context);
  }
}

/** "remove_symbols_by_mask" — payload: positionMask (required). */
export class RemoveSymbolsByMaskActionHandler implements RoundActionHandler {
  public readonly actionTypeName: string = CanonicalActionTypeNames.removeSymbolsByMask;

  public constructor(private readonly present: (positionMask: string, context: ReplayContext) => Promise<void>) {}

  public async handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void> {
    const positionMask = requireStringField(this.actionTypeName, roundAction.actionPayload, "positionMask");
    await this.present(positionMask, context);
  }
}

/** "update_total_win_meter" — payload: winAmount (required). Display authority, not wallet authority. */
export class UpdateTotalWinMeterActionHandler implements RoundActionHandler {
  public readonly actionTypeName: string = CanonicalActionTypeNames.updateTotalWinMeter;

  public constructor(private readonly present: (totalWinAmount: string, context: ReplayContext) => Promise<void>) {}

  public async handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void> {
    const winAmount = requireMinorAmountField(this.actionTypeName, roundAction.actionPayload, "winAmount");

    // The running figure until the event ends: RoundReplayEngine then records the event's
    // accumulatedWinAmountAfterEvent, and the round's last event always carries it (decision 26), so
    // the replay's final win is the snapshot's total, never the meter's.
    context.finalWinAmount = winAmount;

    await this.present(winAmount, context);
  }
}

/** "needle_award" — payload: positionIndex, winAmount (required); highlightMask (optional). */
export class NeedleAwardActionHandler implements RoundActionHandler {
  public readonly actionTypeName: string = CanonicalActionTypeNames.needleAward;

  public constructor(private readonly present: (input: NeedleAwardInput, context: ReplayContext) => Promise<void>) {}

  public async handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void> {
    const payload = roundAction.actionPayload;
    const positionIndex = requireStringField(this.actionTypeName, payload, "positionIndex");
    const winAmount = requireMinorAmountField(this.actionTypeName, payload, "winAmount");
    const highlightMask = optionalStringField(payload, "highlightMask");
    await this.present({ positionIndex, winAmount, highlightMask }, context);
  }
}

/** "symbol_pop_value" — payload: value (required). */
export class SymbolPopValueActionHandler implements RoundActionHandler {
  public readonly actionTypeName: string = CanonicalActionTypeNames.symbolPopValue;

  public constructor(private readonly present: (input: SymbolPopValueInput, context: ReplayContext) => Promise<void>) {}

  public async handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void> {
    const value = requireStringField(this.actionTypeName, roundAction.actionPayload, "value");
    await this.present({ value }, context);
  }
}

/** "add_feature_multiplier" — payload: multiplierAdded, totalMultiplier (required); positionIndex (optional). */
export class AddFeatureMultiplierActionHandler implements RoundActionHandler {
  public readonly actionTypeName: string = CanonicalActionTypeNames.addFeatureMultiplier;

  public constructor(private readonly present: (input: MultiplierAddedInput, context: ReplayContext) => Promise<void>) {}

  public async handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void> {
    const payload = roundAction.actionPayload;
    const multiplierAdded = requireStringField(this.actionTypeName, payload, "multiplierAdded");
    const totalMultiplier = requireStringField(this.actionTypeName, payload, "totalMultiplier");
    const positionIndex = optionalStringField(payload, "positionIndex");
    await this.present({ positionIndex, multiplierAdded, totalMultiplier }, context);
  }
}

/** "collect_feature_multiplier" — payload: multiplierAdded, collectedSum (required). */
export class CollectFeatureMultiplierActionHandler implements RoundActionHandler {
  public readonly actionTypeName: string = CanonicalActionTypeNames.collectFeatureMultiplier;

  public constructor(private readonly present: (multiplierAdded: string, collectedSum: string, context: ReplayContext) => Promise<void>) {}

  public async handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void> {
    const payload = roundAction.actionPayload;
    const multiplierAdded = requireStringField(this.actionTypeName, payload, "multiplierAdded");
    const collectedSum = requireStringField(this.actionTypeName, payload, "collectedSum");
    await this.present(multiplierAdded, collectedSum, context);
  }
}

/**
 * Format B: `positionMask` (0/1 string over the linearized positions) + `values` (one per "1" bit,
 * assigned to the "1" positions in increasing index order).
 */
function deriveMultiplierEntriesFromMask(actionTypeName: string, positionMask: string, values: string[]): VisibleMultiplierEntry[] {
  const oneIndices: number[] = [];
  for (let i = 0; i < positionMask.length; i++) {
    const ch = positionMask[i];
    if (ch !== "0" && ch !== "1") {
      throw new ActionPayloadValidationError(actionTypeName, "positionMask", "Mask must contain only '0' and '1'.");
    }
    if (ch === "1") oneIndices.push(i);
  }
  if (oneIndices.length !== values.length) {
    throw new ActionPayloadValidationError(actionTypeName, "values", `values.length (${values.length}) must match number of '1' bits in positionMask (${oneIndices.length}).`);
  }
  return oneIndices.map((positionIndex, valueIndex) => ({ positionIndex: String(positionIndex), multiplierValue: values[valueIndex] }));
}

/** "present_visible_multipliers" — Format B payload: positionMask, values (required). */
export class PresentVisibleMultipliersActionHandler implements RoundActionHandler {
  public readonly actionTypeName: string = CanonicalActionTypeNames.presentVisibleMultipliers;

  public constructor(private readonly present: (entries: VisibleMultiplierEntry[], context: ReplayContext) => Promise<void>) {}

  public async handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void> {
    const payload = roundAction.actionPayload;
    const positionMask = requireStringField(this.actionTypeName, payload, "positionMask");
    const values = requireStringArrayField(this.actionTypeName, payload, "values");
    await this.present(deriveMultiplierEntriesFromMask(this.actionTypeName, positionMask, values), context);
  }
}

/** "collect_grid_multipliers" — Format B payload: positionMask, values, collectedSum (required). */
export class CollectGridMultipliersActionHandler implements RoundActionHandler {
  public readonly actionTypeName: string = CanonicalActionTypeNames.collectGridMultipliers;

  public constructor(private readonly present: (entries: VisibleMultiplierEntry[], collectedSum: string, context: ReplayContext) => Promise<void>) {}

  public async handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void> {
    const payload = roundAction.actionPayload;
    const positionMask = requireStringField(this.actionTypeName, payload, "positionMask");
    const values = requireStringArrayField(this.actionTypeName, payload, "values");
    const collectedSum = requireStringField(this.actionTypeName, payload, "collectedSum");
    await this.present(deriveMultiplierEntriesFromMask(this.actionTypeName, positionMask, values), collectedSum, context);
  }
}

/** "present_spin_win_multiplication" — payload: baseWinAmount, multiplierApplied, finalWinAmount (required). */
export class PresentSpinWinMultiplicationActionHandler implements RoundActionHandler {
  public readonly actionTypeName: string = CanonicalActionTypeNames.presentSpinWinMultiplication;

  public constructor(
    private readonly present: (baseWinAmount: string, multiplierApplied: string, finalWinAmount: string, context: ReplayContext) => Promise<void>,
  ) {}

  public async handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void> {
    const payload = roundAction.actionPayload;
    const baseWinAmount = requireMinorAmountField(this.actionTypeName, payload, "baseWinAmount");
    const multiplierApplied = requireStringField(this.actionTypeName, payload, "multiplierApplied");
    const finalWinAmount = requireMinorAmountField(this.actionTypeName, payload, "finalWinAmount");
    await this.present(baseWinAmount, multiplierApplied, finalWinAmount, context);
  }
}

/** "present_scatter_state" — engine-validated (scatter payload parser). */
export class PresentScatterStateActionHandler implements RoundActionHandler {
  public readonly actionTypeName: string = CanonicalActionTypeNames.presentScatterState;

  public constructor(private readonly present: (input: ScatterStatePresentationInput, context: ReplayContext) => Promise<void>) {}

  public async handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void> {
    const parsed = parsePresentScatterStatePayload(this.actionTypeName, roundAction.actionPayload);
    await this.present({ positionIndices: parsed.indices, scatterCount: parsed.scatterCount, scatterSymbolIdentifier: parsed.scatterSymbolIdentifier }, context);
  }
}

/** "bonus_feature_won" — the "feature won" moment; NOT the same as the "feature_enter" event. */
export class BonusFeatureWonActionHandler implements RoundActionHandler {
  public readonly actionTypeName: string = CanonicalActionTypeNames.bonusFeatureWon;

  public constructor(private readonly present: (featureIdentifier: string, featureCountAwarded: string, context: ReplayContext) => Promise<void>) {}

  public async handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void> {
    const parsed = parseBonusFeatureWonPayload(this.actionTypeName, roundAction.actionPayload);
    await this.present(parsed.featureIdentifier, parsed.featureCountAwarded, context);
  }
}

/** "present_big_win" — engine-validated (big-win payload parser). */
export class PresentBigWinActionHandler implements RoundActionHandler {
  public readonly actionTypeName: string = CanonicalActionTypeNames.presentBigWin;

  public constructor(private readonly present: (input: PresentBigWinInput, context: ReplayContext) => Promise<void>) {}

  public async handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void> {
    const parsed = parsePresentBigWinPayload(this.actionTypeName, roundAction.actionPayload);
    await this.present(
      { levelIdentifier: parsed.levelIdentifier, spinWinAmount: parsed.spinWinAmount, betAmount: parsed.betAmount, winRatioLabel: parsed.winRatioLabel },
      context,
    );
  }
}

/**
 * "spin_boundary_start" — the server-authoritative begin of a single spin presentation. Records the
 * active boundary on the context (the end may come in a later event).
 */
export class SpinBoundaryStartActionHandler implements RoundActionHandler {
  public readonly actionTypeName: string = CanonicalActionTypeNames.spinBoundaryStart;

  public constructor(private readonly present: (input: SpinBoundaryStartInput, context: ReplayContext) => Promise<void>) {}

  public async handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void> {
    const parsed = validateSpinBoundaryStartPayloadOrThrow(roundAction.actionPayload);
    context.activeSpinBoundaryIdentifier = parsed.spinBoundaryIdentifier;
    context.activeSpinModeIdentifier = parsed.spinModeIdentifier ?? null;
    await this.present({ spinBoundaryIdentifier: parsed.spinBoundaryIdentifier, spinModeIdentifier: parsed.spinModeIdentifier }, context);
  }
}

/** "spin_boundary_end" — the end of the same spin presentation; clears the active boundary after presenting. */
export class SpinBoundaryEndActionHandler implements RoundActionHandler {
  public readonly actionTypeName: string = CanonicalActionTypeNames.spinBoundaryEnd;

  public constructor(private readonly present: (input: SpinBoundaryEndInput, context: ReplayContext) => Promise<void>) {}

  public async handleAction(roundAction: RoundAction, context: ReplayContext): Promise<void> {
    const parsed = validateSpinBoundaryEndPayloadOrThrow(roundAction.actionPayload);

    // A mismatching end id is worth a warning during fixture authoring.
    if (context.activeSpinBoundaryIdentifier && context.activeSpinBoundaryIdentifier !== parsed.spinBoundaryIdentifier) {
      context.logDebugMessage?.(`[SpinBoundary] WARNING: end id "${parsed.spinBoundaryIdentifier}" != active "${context.activeSpinBoundaryIdentifier}"`);
    }

    await this.present({ spinBoundaryIdentifier: parsed.spinBoundaryIdentifier, spinWinAmount: parsed.spinWinAmount }, context);

    context.activeSpinBoundaryIdentifier = null;
    context.activeSpinModeIdentifier = null;
  }
}
